/* eslint-disable @typescript-eslint/no-restricted-imports */
import { Controller } from 'react-hook-form';

import Alert from '~/common/components/Alerts/Alert';
import Divider from '~/common/mui/Divider';
import theme from '~/theme';

import {
  FormBuilderCheckbox,
  FormBuilderCheckboxGroup,
} from './FormBuilderInputs/Checkboxes';
import { FbDatePicker } from './FormBuilderInputs/DatePicker';
import {
  FormBuilderCurrencyInput,
  FormBuilderHiddenInput,
  FormBuilderNumberInput,
  FormBuilderPatternedInput,
  FormBuilderRightElementInput,
  FormBuilderTextAreaInput,
  FormBuilderTextInput,
} from './FormBuilderInputs/Inputs';
import {
  FormBuilderRadioButtonGroup,
  FormBuilderRadioButtons,
} from './FormBuilderInputs/RadioButtons';
import { FormBuilderSelect } from './FormBuilderInputs/Select';
import InfoText from './FormBuilderNonInputs/InfoText';

type InputMapperProps = {
  type?: string | number;
  name?: string;
  control?: any;
  rules?: object;
  error?: any;
  text?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  tabIndex?: number;
  isInvalid?: boolean;
  getValues?: (name: string) => any;
  value?: any;
  status?: 'error' | 'info' | 'warning' | 'success';
  title?: string;
  description?: string;
  onBlur?: (value: any) => void;
  onChangeHandler?: (value: any) => void;
  disabled?: boolean;
  isNotice?: boolean;
};

export default function InputMapper({
  type,
  name,
  control,
  rules,
  error,
  ...props
}: InputMapperProps) {
  const inputMap = {
    // Text Input
    ['number']: <FormBuilderNumberInput name={name} {...props} />,
    ['text']:
      control && name ? (
        <Controller
          name={name}
          control={control}
          rules={rules}
          defaultValue={props.value || ''}
          render={({ onChange, onBlur, name }, { invalid }) => {
            return (
              <FormBuilderTextInput
                {...props}
                name={name}
                onChangeHandler={(e) => {
                  if (props.onChangeHandler) {
                    props.onChangeHandler(e);
                  } else {
                    onChange(e);
                  }
                }}
                onBlur={(value) => {
                  if (props.onBlur) {
                    props.onBlur(value);
                  }
                  onBlur();
                }}
                isInvalid={invalid}
              />
            );
          }}
        />
      ) : (
        <FormBuilderTextInput name={name} {...props} />
      ),
    ['textarea']: <FormBuilderTextAreaInput name={name} {...props} />,
    ['currency']:
      control && name ? (
        <Controller
          name={name}
          control={control}
          rules={rules}
          defaultValue={props.value || ''}
          render={({ onChange, onBlur, value, name }) => (
            <FormBuilderCurrencyInput
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              {...props}
            />
          )}
        />
      ) : (
        <FormBuilderCurrencyInput
          {...props}
          name={name}
          getValues={props.getValues}
        />
      ),
    ['inputWithPercentage']: (
      <FormBuilderRightElementInput name={name} {...props} />
    ),
    ['patternedInput']: <FormBuilderPatternedInput name={name} {...props} />,
    ['hidden']: <FormBuilderHiddenInput name={name} {...props} />,
    // Select Input
    ['select']:
      control && name ? (
        <Controller
          name={name}
          control={control}
          rules={rules}
          defaultValue={props.value || ''}
          render={({ onChange, name }, { invalid }) => {
            return (
              <FormBuilderSelect
                {...props}
                name={name}
                onChangeHandler={(value) => {
                  if (props.onChangeHandler) {
                    props.onChangeHandler(value); // call your custom handler
                  } else {
                    onChange(value); // update form state if no custom handler
                  }
                }}
                isInvalid={invalid}
              />
            );
          }}
        />
      ) : (
        <FormBuilderSelect name={name} {...props} />
      ),
    // Spacer or filler
    ['spacer']: <div style={{ height: '1rem' }} />,
    ['divider']: (
      <Divider
        sx={{
          border: '1px solid',
          borderColor: theme.colors.gray[300],
          marginBottom: '1rem',
          marginTop: '1rem',
        }}
      />
    ),
    // Checkboxes
    ['checkbox']: (
      <FormBuilderCheckbox name={name} {...props} control={control} />
    ),
    ['checkboxGroup']: <FormBuilderCheckboxGroup name={name} {...props} />,
    ['datepicker']:
      control && name ? (
        <Controller
          name={name}
          control={control}
          rules={rules}
          defaultValue={props.value || null}
          render={({ onChange, onBlur, name }, { invalid }) => {
            return (
              <FbDatePicker
                {...props}
                name={name}
                getValues={props.getValues}
                onChangeHandler={onChange}
                onBlur={onBlur}
                isInvalid={invalid}
              />
            );
          }}
        />
      ) : (
        <FbDatePicker {...props} name={name} getValues={props.getValues} />
      ),
    // Radio
    ['radio']: <FormBuilderRadioButtons {...props} />,
    ['radioGroup']: <FormBuilderRadioButtonGroup {...props} />,
    // Misc / Non Inputs
    ['infoText']: (
      <InfoText
        text={props.text}
        style={props.style}
        dataTestId={props.dataTestId}
      />
    ),
    ['alert']: (
      <Alert
        status={props.status}
        title={props.title}
        isNotice={props.isNotice}
        sx={{ my: 4 }}
      >
        {props.description}
      </Alert>
    ),
  };
  if (inputMap[type]) {
    return inputMap[type];
  } else {
    return inputMap['spacer'];
  }
}
